import { 
    useCallback, 
    useEffect, 
    useState, 
    lazy,
    Suspense
} from "react"

import { 
    Box,
    // Button,
    Stack,
    Grid,
    Paper,
    Skeleton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Menu,
    MenuItem,
    // Typography,
} from "@mui/material"
// import { FileDownload } from "@mui/icons-material"

import { useNavigate, useParams } from "react-router-dom"

import { Header } from "../../components/Header/Header"
import { PDialog } from "../../components/PDialog/PDialog"
import { ButtonBar } from "../../components/ButtonBar/ButtonBar"
import { List, type ListItemData } from "../../components/List/List"

import type { PMPPPredictionJob } from "../../types/Types"

import { strings } from "../../config/strings"

import { parseCSV, downloadFileResult, type CSVResult } from "../../utils/csvUtils"
import { timeAgo, trainingDateStringConversion } from "../../utils/stringsUtils"
import { deletePredictionJob, downloadPredictionJobResults, getPredictionJob } from "../../utils/api"


const HSpreadsheet = lazy(
  () => import("../../components/HSpreadsheet/HSpreadsheet")
)

export const PredictionPage = () => {

    //State

    const [loadingData, setLoadingData] = useState<boolean>(false)
    const [loadingCSV, setLoadingCSV] = useState<boolean>(false)
    const [loadingDelete, setLoadingDelete] = useState<boolean>(false)

    const [downloadInProgress, setDownloadInProgress] = useState<boolean>(false)

    const [currentPredictionJob, setCurrentPredictionJob] = useState<PMPPPredictionJob | undefined>()
    
    const [rawCSV, setRawCSV] = useState<string | undefined>()
    const [csvResults, setCSVResults] = useState<CSVResult | undefined>()

    const [openModelsInfo, setOpenModelsInfo] = useState<boolean>(false)
    const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false)

    const [downloadMenuAnchorEl, setDownloadMenuAnchorEl] = useState<undefined | HTMLElement>()


    //Hooks
    
    const { job_id } = useParams()
    if(!job_id) return <></>


    const navigate = useNavigate()


    //Callbacks

    const loadData = useCallback( async () => {
        try {
            if(!job_id) return

            setLoadingData(true)
            const predictionJobRes = await getPredictionJob(parseInt(job_id))
            setCurrentPredictionJob(predictionJobRes)
        }
        catch(e: any) {
            console.log(e.data)
        }
        finally {
            setLoadingData(false)
        }

    }, [])

    const loadCSV = useCallback( async () => {
        try {
            if(!job_id) return

            setLoadingCSV(true)
            const res = await downloadPredictionJobResults(parseInt(job_id), 'csv')
            setRawCSV(res)
            setCSVResults(parseCSV(res))
        }
        catch(e: any) {
            console.log(e.data)
        }
        finally {
            setLoadingCSV(false)
        }

    }, [])
    
    //Effects

    useEffect( () => {
        loadData()
    }, [])

    useEffect( () => {
        loadCSV()
    }, [])


    //Handlers

    const handleJobDeletion = async () => {
        try {
            setLoadingDelete(true)
            await deletePredictionJob(parseInt(job_id))
            navigate("/")
        }
        catch (e){
            console.log(e)
        }
        finally {
            setLoadingDelete(false)
        }
    }

    const handleCloseModelsInfo = () => setOpenModelsInfo(false)

    const handleDownloadMenuClose = () => setDownloadMenuAnchorEl(undefined)
    
    const handleFileDownload = async (format: 'csv' | 'sdf') => {
        if(!currentPredictionJob)
            return

        try {
            setDownloadInProgress(true)
            setDownloadMenuAnchorEl(undefined)
            const res = await downloadPredictionJobResults(currentPredictionJob.id, format)
            downloadFileResult(res, `${currentPredictionJob.name}.${format}` )
        }
        catch(e) {
            console.log(e)
        }
        finally {
            setDownloadInProgress(false)
        }
    }

    //Render

    const renderPredictionJobInfo = () => (
        <Paper sx={{p:2}}>
            <List 
                loading={loadingData}
                items={[
                    {
                        title: strings.state,
                        content: currentPredictionJob?.state,
                        loading: loadingData
                    },
                    {
                        title: strings.creation_date,
                        content: timeAgo(currentPredictionJob?.creation_date),
                        loading: loadingData
                    }
                ]}
            />
        </Paper>
    )

    const renderButtonBar = () => {

        return (
            <>
                <ButtonBar buttons={[
                    {
                        label: strings.models_information,
                        color: 'primary',
                        disabled: loadingData || loadingCSV,
                        onClick: () => setOpenModelsInfo(true)
                    },
                    {
                        label: strings.action_download_results,
                        color: 'primary',
                        disabled: loadingData || loadingCSV,
                        loading: downloadInProgress,
                        onClick: (e) => {
                            if(!currentPredictionJob)
                                return
                            setDownloadMenuAnchorEl(e.currentTarget)
                        }
                    },
                    {
                        label: strings.action_delete,
                        color: 'error',
                        disabled: loadingData || loadingCSV,
                        onClick: () => setOpenDeleteDialog(true)
                    }
                ]}/>
                <Menu
                    anchorEl={downloadMenuAnchorEl}
                    open={Boolean(downloadMenuAnchorEl)}
                    onClose={handleDownloadMenuClose}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                >
                    <MenuItem onClick={() => handleFileDownload('csv')}>{strings.csv_format}</MenuItem>
                    <MenuItem onClick={() => handleFileDownload('sdf')}>{strings.sdf_format}</MenuItem>
                </Menu>
            </>
        )
    }


    const renderModelsInfo = () => {
        if(!currentPredictionJob?.models) 
            return

        return (
            <Dialog open={openModelsInfo} onClose={handleCloseModelsInfo} fullWidth maxWidth='xl'>
                <DialogTitle>{strings.models_information}</DialogTitle>
                <DialogContent>
                {
                    <Grid container spacing={2}>
                        {
                            Object.keys(currentPredictionJob.models).map( modelKey => {

                                const model = currentPredictionJob.models![modelKey]

                                const modelProperties: ListItemData[] = []
                                
                                if(model['training_data_date']){
                                    modelProperties.push({title: strings.training_date, content: trainingDateStringConversion(model['training_data_date'])})
                                }       
                                if(model['kendall_tau']){
                                    modelProperties.push({title: strings.kendall_tau, content: parseFloat(model['kendall_tau']).toFixed(4)})
                                }
                                if(model['pearson_r']){
                                    modelProperties.push({title: strings.pearson_r, content: parseFloat(model['pearson_r']).toFixed(4)})
                                }
                                if(model['spearman_r']){
                                    modelProperties.push({title: strings.spearman_r, content: parseFloat(model['spearman_r']).toFixed(4)})
                                }
                                                
                                return (
                                    <Grid size={4} key={modelKey}>
                                        <Paper sx={{p:2}}>
                                            <List 
                                                title={modelKey}
                                                items={modelProperties}
                                            /> 
                                        </Paper>
                                    </Grid>
                                )
                            })
                        }
                    </Grid>
                }
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModelsInfo}>
                            {strings.action_close}
                    </Button>
                </DialogActions>
            </Dialog>
        )
    }

    const renderCSV = () => {

        const skeleton = (
            <Paper sx={{p:2, height:'100%', boxSizing:'border-box'}}>
                <Skeleton variant="rounded" height='100%'/>
            </Paper>
        )

        if(loadingCSV || !csvResults || !rawCSV) { 
            return skeleton
        }
        return (
            <Paper sx={{height:'100%', overflow: 'hidden'}}>
                <Suspense fallback={skeleton}>
                    <HSpreadsheet  
                        csvResults={csvResults}
                        smilesColumn={currentPredictionJob?.smiles_column}
                    />
                </Suspense>
            </Paper>
        )
    }

    return (
        <Box sx={{
            height: '100%',
            maxWidth: '95%',
            margin: 'auto',
            py:2,
            boxSizing: 'border-box'
        }}>
            <Stack height='100%' spacing={2} >
                <Grid container direction='row' spacing={2}>
                    <Grid size={6}>
                        <Header 
                            loading={loadingData}
                            title={currentPredictionJob?.name??''} 
                            breadcrumbs={[{
                                title: strings.prediction_jobs,
                                link: "/"
                            }]}
                        />
                    </Grid>
                    <Grid size={6}>
                        {renderPredictionJobInfo()}
                    </Grid>
                </Grid>
                <Grid size={12}>
                    {renderButtonBar()}
                </Grid>               
                <Grid size={12} height='100%'>
                    {renderCSV()}
                </Grid>
            </Stack>
            <PDialog 
                open={openDeleteDialog} 
                onClose={() => setOpenDeleteDialog(false)}
                title={strings.dialog_prediction_job_delete_title}
                body={strings.dialog_prediction_job_delete_body}
                onSubmit={handleJobDeletion}
                submitButtonLabel={strings.action_delete}
                submitButtonColor="error"
                loading={loadingDelete}
            />
            {renderModelsInfo()}
        </Box>  
    )
}